import { useState, useRef } from "react";
import "../styles/gallery.css";
import { ADMIN_PASSWORD } from "../data/constants";

export default function Gallery({ gallery, setGallery, adminMode, setAdminMode }) {
  const [showLogin, setShowLogin] = useState(false);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [newCaption, setNewCaption] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef();

  const handleLogin = () => {
    if (pw === ADMIN_PASSWORD) {
      setAdminMode(true); setShowLogin(false); setPwError(""); setPw("");
    } else {
      setPwError("Incorrect password.");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setUploadError("Please select a valid image file."); return; }
    setUploadError("");
    setUploading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setGallery((g) => [...g, { id: Date.now(), url: ev.target.result, caption: newCaption || "New Upload" }]);
      setNewCaption("");
      setUploading(false);
      fileRef.current.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleUrlAdd = () => {
    if (!newUrl.trim()) { setUploadError("Please enter a valid image URL."); return; }
    setUploadError("");
    setGallery((g) => [...g, { id: Date.now(), url: newUrl.trim(), caption: newCaption || "New Photo" }]);
    setNewUrl("");
    setNewCaption("");
  };

  const deletePhoto = (id) => setGallery((g) => g.filter((p) => p.id !== id));

  return (
    <div className="gallery">

      {/* Header */}
      <div className="gallery__header">
        <p className="gallery__eyebrow">OUR WORK</p>
        <h1 className="gallery__title">Gallery</h1>
        <p className="gallery__subtitle">A look at our team in action — servicing fleets across the region.</p>
        {adminMode && (
          <div className="gallery__admin-badge">✅ Admin Mode Active — You can add and delete photos</div>
        )}
      </div>

      {/* Admin Login Modal */}
      {showLogin && (
        <div className="gallery__modal-overlay">
          <div className="gallery__modal">
            <h2 className="gallery__modal-title">Admin Login</h2>
            <input
              type="password"
              placeholder="Password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              className="gallery__modal-input"
            />
            {pwError && <div className="gallery__modal-error">{pwError}</div>}
            <div className="gallery__modal-actions">
              <button className="gallery__modal-login" onClick={handleLogin}>Login</button>
              <button className="gallery__modal-cancel" onClick={() => { setShowLogin(false); setPw(""); setPwError(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Upload Panel */}
      {adminMode && (
        <div className="gallery__admin-panel">
          <h3 className="gallery__admin-title">📸 Add New Photo</h3>
          <div className="gallery__admin-fields">

            <div className="gallery__admin-field gallery__admin-field--full">
              <label className="gallery__admin-label">Photo Caption</label>
              <input
                value={newCaption}
                onChange={(e) => setNewCaption(e.target.value)}
                placeholder="e.g. Fleet Oil Change – March 2025"
                className="gallery__admin-input"
              />
            </div>

            <div className="gallery__admin-field">
              <label className="gallery__admin-label">Option 1 — Paste Image URL</label>
              <input
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://..."
                className="gallery__admin-input"
              />
            </div>

            <div className="gallery__admin-field">
              <label className="gallery__admin-label">Option 2 — Upload from Device</label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="gallery__admin-file-input"
              />
            </div>

          </div>

          {uploadError && <div className="gallery__admin-error">{uploadError}</div>}

          <div className="gallery__admin-actions">
            <button className="gallery__admin-add" onClick={handleUrlAdd}>
              ＋ Add from URL
            </button>
            <button className="gallery__admin-upload" onClick={() => fileRef.current.click()}>
              {uploading ? "Uploading..." : "📁 Upload File"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFileUpload} style={{ display: "none" }} />
          </div>

          <p className="gallery__admin-tip">💡 Tip: Enter a caption first, then add your photo using either method above.</p>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="gallery__grid">
        {gallery.map((photo) => (
          <div key={photo.id} className={"gallery__item" + (adminMode ? " gallery__item--admin" : "")}
            onClick={() => !adminMode && setLightbox(photo)}>
            <img src={photo.url} alt={photo.caption} />
            <div className="gallery__item-caption">{photo.caption}</div>
            {adminMode && (
              <div className="gallery__item-overlay">
                <button
                  className="gallery__item-delete"
                  onClick={(e) => { e.stopPropagation(); deletePhoto(photo.id); }}>
                  🗑 Delete Photo
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty state */}
      {gallery.length === 0 && (
        <div className="gallery__empty">No photos yet. Use the panel above to add your first photo.</div>
      )}

      {/* Admin Login Button */}
      <div className="gallery__admin-login-wrap">
        {!adminMode
          ? <button className="gallery__admin-login-btn" onClick={() => setShowLogin(true)}>🔒 Admin Login</button>
          : <button className="gallery__admin-exit-btn" onClick={() => setAdminMode(false)}>Exit Admin Mode</button>
        }
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="gallery__lightbox" onClick={() => setLightbox(null)}>
          <div className="gallery__lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.caption} className="gallery__lightbox-img" />
            <p className="gallery__lightbox-caption">{lightbox.caption}</p>
            <button className="gallery__lightbox-close" onClick={() => setLightbox(null)}>Close</button>
          </div>
        </div>
      )}

    </div>
  );
}